// Bloco "A base (semana 1)": faturamento e lucro dos 12 meses fechados, lidos ao vivo da DRE.
// Só aparece para quem tem `financeiro.dre.ver`; sem a permissão não desenha nada (nem o título).
// O texto é feito por regra, sem IA (frasesDaBase). Erro de leitura vira um aviso curto
// e o resto da página segue.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Card, Col, Row, Skeleton, Statistic, Typography } from 'antd';
import { Bar, BarChart, CartesianGrid, LabelList, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { usePerms } from '../../hooks/usePerms';
import { hojeIso } from '../../utils/dataFmt';
import {
  buscarDreSetores, buscarFaturamento12m, ErroDre, type DreSetores, type Faturamento12m,
} from '../administrativo-plano-acao/paginas/apiDre';
import {
  blocosDeMeses, janelaDoFiltro, juntarBlocos, MAX_MESES_FATURAMENTO, pctTexto, rotuloMes,
} from '../administrativo-plano-acao/paginas/dadosSetores';
import { frasesDaBase, inteiroBr, pct, reais, reaisCurto, resumoDaBase, type ResumoBase } from './ondeGanhaRegras';

const { Title, Text, Paragraph } = Typography;
const PERM = 'financeiro.dre.ver';

interface Barra { nome: string; valor: number; fill: string; rotulo: string; detalhe?: string }

const cortar = (t: string, n = 24): string => (t.length > n ? `${t.slice(0, n - 1)}…` : t);

/** Lê a DRE por setor; se a conexão cair no meio (muitos meses de uma vez), refaz em blocos de 3 meses. */
async function lerDre(de: string, ate: string): Promise<DreSetores> {
  try {
    return await buscarDreSetores({ de, ate, agrupar: 'setores' });
  } catch (e: any) {
    const lento = e instanceof ErroDre && (e.status == null || e.status >= 500);
    if (!lento) throw e;
    const partes: DreSetores[] = [];
    for (const b of blocosDeMeses(de, ate, 3)) partes.push(await buscarDreSetores({ ...b, agrupar: 'setores' }));
    return juntarBlocos(partes);
  }
}

function TooltipBarra({ active, payload }: { active?: boolean; payload?: any[] }) {
  if (!active || !payload || !payload.length) return null;
  const b: Barra = payload[0].payload;
  return (
    <div style={{ background: '#fff', border: '1px solid #d9d9d9', borderRadius: 6, padding: '8px 12px', fontSize: 12, boxShadow: '0 2px 8px rgba(0,0,0,.12)' }}>
      <div style={{ fontWeight: 600 }}>{b.nome}</div>
      <div>{reais(b.valor)}</div>
      {b.detalhe ? <div style={{ color: '#595959' }}>{b.detalhe}</div> : null}
    </div>
  );
}

function BarrasHorizontais({ dados, rotuloEixo }: { dados: Barra[]; rotuloEixo: string }) {
  const altura = Math.max(120, dados.length * 34 + 24);
  return (
    <div style={{ overflowX: 'auto' }}>
      <div role="img" aria-label={rotuloEixo} style={{ minWidth: 460, height: altura }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={dados} layout="vertical" margin={{ top: 4, right: 96, left: 0, bottom: 4 }}>
          <CartesianGrid horizontal={false} stroke="#eee" strokeDasharray="3 3" />
          <XAxis type="number" hide />
          <YAxis type="category" dataKey="nome" width={176} tick={{ fontSize: 12 }} tickFormatter={(v: string) => cortar(v, 32)} interval={0} />
          <Tooltip content={(p: any) => <TooltipBarra active={p.active} payload={p.payload} />} cursor={{ fill: 'rgba(0,0,0,.04)' }} />
          <ReferenceLine x={0} stroke="#8c8c8c" />
          <Bar dataKey="valor" fill="#1677ff" maxBarSize={22} isAnimationActive={false}>
            <LabelList dataKey="rotulo" position="right" style={{ fontSize: 12, fill: '#262626' }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      </div>
    </div>
  );
}

/** `onResumo`: a página usa o mesmo resumo em "O que aprendemos" (investido e lucro final). */
export default function BaseFinanceira({ onResumo }: { onResumo?: (r: ResumoBase | null, periodo: string) => void } = {}) {
  const { pode, carregando: carregandoPerms } = usePerms();
  const liberado = !carregandoPerms && pode(PERM);

  const janela = useMemo(
    () => janelaDoFiltro({ modo: 'ultimos12', incluirAtual: false }, hojeIso(), MAX_MESES_FATURAMENTO),
    [],
  );
  const de = janela.ok ? janela.de : null;
  const ate = janela.ok ? janela.ate : null;

  const [fat, setFat] = useState<Faturamento12m | null>(null);
  const [dre, setDre] = useState<DreSetores | null>(null);
  const [erroFat, setErroFat] = useState<string | null>(null);
  const [erroDre, setErroDre] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  const pedido = useRef(0);

  useEffect(() => {
    if (!liberado || !de || !ate) return;
    const meu = ++pedido.current;
    setCarregando(true); setErroFat(null); setErroDre(null);
    const f = buscarFaturamento12m({ de, ate, agrupar: 'setores', grupo: 'cliente' })
      .then((d) => { if (meu === pedido.current) setFat(d); })
      .catch((e: any) => { if (meu === pedido.current) setErroFat(String(e?.message || e)); });
    const l = lerDre(de, ate)
      .then((d) => { if (meu === pedido.current) setDre(d); })
      .catch((e: any) => { if (meu === pedido.current) setErroDre(String(e?.message || e)); });
    Promise.all([f, l]).finally(() => { if (meu === pedido.current) setCarregando(false); });
  }, [liberado, de, ate]);

  const resumo = useMemo(() => resumoDaBase(fat, dre), [fat, dre]);
  const frases = useMemo(() => frasesDaBase(resumo), [resumo]);
  const periodoTexto = janela.ok ? `${rotuloMes(janela.de)} a ${rotuloMes(janela.ate)}` : '';
  useEffect(() => {
    if (onResumo) onResumo(liberado && (fat || dre) ? resumo : null, periodoTexto);
  }, [onResumo, liberado, fat, dre, resumo, periodoTexto]);

  const barrasSetor: Barra[] = useMemo(
    () => resumo.setoresFat.map((s) => ({ nome: s.nome, valor: s.valor, fill: s.cor, rotulo: `${reaisCurto(s.valor)} · ${pct(s.pct)}` })),
    [resumo],
  );
  const barrasCliente: Barra[] = useMemo(
    () => resumo.maiores.map((c) => ({ nome: c.nome, valor: c.valor, fill: c.cor, rotulo: `${reaisCurto(c.valor)} · ${pct(c.pct)}` })),
    [resumo],
  );
  const barrasLucro: Barra[] = useMemo(
    () => resumo.lucroSetor.map((s) => ({
      nome: s.nome, valor: s.lucro, fill: s.lucro < 0 ? '#cf1322' : s.cor,
      rotulo: `${reaisCurto(s.lucro)}${s.margem != null ? ` · margem ${pctTexto(s.margem, 0)}` : ''}`,
      detalhe: s.margem != null ? `Margem sobre a receita do setor: ${pctTexto(s.margem)}` : undefined,
    })),
    [resumo],
  );

  // Sem permissão: nada na tela (nem o título).
  if (!liberado) return null;

  const periodo = periodoTexto;

  return (
    <section aria-labelledby="og-base-titulo" style={{ marginTop: 32 }}>
      <Text type="secondary" style={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 12 }}>Semana 1</Text>
      <Title level={3} id="og-base-titulo" style={{ marginTop: 0 }}>A base (semana 1)</Title>
      <Paragraph type="secondary">
        Faturamento e lucro dos 12 meses fechados{periodo ? `, de ${periodo}` : ''}. Os números vêm da DRE na hora em que você abre a página.
      </Paragraph>

      {!janela.ok ? <Alert type="warning" showIcon title={janela.aviso} style={{ marginBottom: 12 }} /> : null}
      {erroFat ? (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }} title="Não consegui ler o faturamento." description={erroFat} />
      ) : null}
      {erroDre ? (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }} title="Não consegui ler o lucro por setor." description={erroDre} />
      ) : null}

      {carregando && !fat && !dre ? <Skeleton active paragraph={{ rows: 6 }} /> : null}

      {fat || dre ? (
        <>
          <Row gutter={[12, 12]}>
            <Col xs={12} lg={8} xl={4}>
              <Card size="small"><Statistic title="Faturamento em 12 meses" value={resumo.faturamento != null ? reais(resumo.faturamento) : '—'} /></Card>
            </Col>
            <Col xs={12} lg={8} xl={4}>
              <Card size="small"><Statistic title="Peso dos 3 maiores clientes" value={resumo.top3Pct != null ? pctTexto(resumo.top3Pct) : '—'} /></Card>
            </Col>
            <Col xs={12} lg={8} xl={4}>
              <Card size="small">
                <Statistic
                  title="Lucro líquido" value={resumo.lucro != null ? reais(resumo.lucro) : '—'}
                  suffix={resumo.margem != null ? <Text type="secondary" style={{ fontSize: 14 }}>margem {pctTexto(resumo.margem)}</Text> : undefined}
                />
              </Card>
            </Col>
            <Col xs={12} lg={8} xl={4}>
              <Card size="small"><Statistic title="Investido (equipamentos, veículos, software)" value={resumo.investido != null ? reais(resumo.investido) : '—'} /></Card>
            </Col>
            <Col xs={12} lg={8} xl={4}>
              <Card size="small">
                <Statistic
                  title="Lucro final (depois dos investimentos)" value={resumo.lucroFinal != null ? reais(resumo.lucroFinal) : '—'}
                  suffix={resumo.margemFinal != null ? <Text type="secondary" style={{ fontSize: 14 }}>margem {pctTexto(resumo.margemFinal)}</Text> : undefined}
                />
              </Card>
            </Col>
            <Col xs={12} lg={8} xl={4}>
              <Card size="small"><Statistic title="Clientes faturados" value={resumo.clientes != null ? inteiroBr(resumo.clientes) : '—'} /></Card>
            </Col>
          </Row>

          {frases.length ? <Paragraph style={{ marginTop: 16, marginBottom: 0 }}>{frases.join(' ')}</Paragraph> : null}

          <Row gutter={[24, 16]} style={{ marginTop: 16 }}>
            {barrasSetor.length ? (
              <Col xs={24} lg={12}>
                <Text strong>Faturamento por setor</Text>
                <div><Text type="secondary">12 meses; valor e parte do total</Text></div>
                <BarrasHorizontais dados={barrasSetor} rotuloEixo="Faturamento por setor" />
              </Col>
            ) : null}
            {barrasCliente.length ? (
              <Col xs={24} lg={12}>
                <Text strong>Maiores clientes</Text>
                <div><Text type="secondary">12 meses; valor e parte do faturamento</Text></div>
                <BarrasHorizontais dados={barrasCliente} rotuloEixo="Maiores clientes" />
              </Col>
            ) : null}
          </Row>

          {barrasLucro.length ? (
            <div style={{ marginTop: 16 }}>
              <Text strong>Lucro líquido por setor</Text>
              <div><Text type="secondary">12 meses; margem sobre a receita do setor. A soma dos setores é igual à DRE.</Text></div>
              <BarrasHorizontais dados={barrasLucro} rotuloEixo="Lucro líquido por setor" />
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
